import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { KanbanBoard } from '../components/KanbanBoard';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { supportTaskService, SupportTaskStatus, SupportTaskType, SupportTaskPriority, TaskProjectScope, getTaskStatusLabel, getTaskTypeLabel, getTaskPriorityLabel, getProjectScopeItems } from '../api';

import { Button, ThemedText, ActionMenu, ConfirmDialog, SkeletonTable, EmptyState } from '../components';
import { ClipboardCheck, Search, ChevronRight } from 'lucide-react';
import { SupportTaskStatusColors, SupportTaskTypeColors, SupportTaskPriorityColors, ProjectScopeColors, StatusPalette } from '../constants/statusColors';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => `${props.$color}20`};
  color: ${props => props.$color};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconStyled = styled.span`
  position: absolute;
  left: 10px;
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.muted};
  pointer-events: none;
`;

const filterControl = `
  height: 34px;
  border-radius: 6px;
  font-size: 13px;
  transition: border-color 0.15s ease;
`;

const SearchInput = styled.input`
  ${filterControl}
  width: 200px;
  padding: 0 10px 0 32px;
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
  &::placeholder { color: ${props => props.theme.colors.muted}; }
`;

const FilterSelect = styled.select`
  ${filterControl}
  padding: 0 26px 0 10px;
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
  &:hover { border-color: ${props => props.theme.colors.tint}40; }
`;

const FilterToggle = styled.button`
  ${filterControl}
  padding: 0 12px;
  border: 1px solid ${props => props.$active ? props.theme.colors.tint : props.theme.colors.border};
  background-color: ${props => props.$active ? `${props.theme.colors.tint}10` : props.theme.colors.background};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;

  &:hover {
    border-color: ${props => props.theme.colors.tint}60;
  }
`;

const ProjectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => `${props.$color}20`};
  color: ${props => props.$color};
`;

const ProjectBadgesContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ViewToggleGroup = styled.div`
  display: flex;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
`;

const ViewToggleButton = styled.button`
  padding: 6px 14px;
  border: none;
  background-color: ${props => props.$active ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.$active ? '#fff' : props.theme.colors.text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.$active ? props.theme.colors.tint : `${props.theme.colors.tint}10`};
  }

  & + & {
    border-left: 1px solid ${props => props.theme.colors.border};
  }
`;


const STATUS_OPTIONS = [
  { value: SupportTaskStatus.OPEN, label: 'Open' },
  { value: SupportTaskStatus.IN_PROGRESS, label: 'In Progress' },
  { value: SupportTaskStatus.ON_HOLD, label: 'On Hold' },
  { value: SupportTaskStatus.RESOLVED, label: 'Resolved' },
  { value: SupportTaskStatus.CLOSED, label: 'Closed' },
  { value: SupportTaskStatus.BACKLOG, label: 'Backlog' },
];

const TYPE_OPTIONS = [
  { value: SupportTaskType.BUG, label: 'Bug' },
  { value: SupportTaskType.FEATURE, label: 'Feature' },
  { value: SupportTaskType.IMPROVEMENT, label: 'Improvement' },
  { value: SupportTaskType.QUESTION, label: 'Question' },
  { value: SupportTaskType.PROBLEM, label: 'Problem' },
];

const PRIORITY_OPTIONS = [
  { value: SupportTaskPriority.LOW, label: 'Low' },
  { value: SupportTaskPriority.MEDIUM, label: 'Medium' },
  { value: SupportTaskPriority.HIGH, label: 'High' },
  { value: SupportTaskPriority.CRITICAL, label: 'Critical' },
];

const getStatusColor = (status) => SupportTaskStatusColors[status] || StatusPalette.slate;
const getTypeColor = (type) => SupportTaskTypeColors[type] || StatusPalette.slate;
const getPriorityColor = (priority) => SupportTaskPriorityColors[priority] || StatusPalette.slate;

const PROJECT_SCOPE_OPTIONS = [
  { value: TaskProjectScope.PORTAL, label: 'Portal' },
  { value: TaskProjectScope.MOBILE_APP, label: 'Mobile App' },
  { value: TaskProjectScope.NONE, label: 'None' },
];

const TABLE_PAGE_SIZE = 20;
const KANBAN_PAGE_SIZE = 200;

const renderProjectBadges = (projectScope, theme) => {
  const items = getProjectScopeItems(projectScope);
  if (items.length === 0) return <span style={{ color: theme.colors.muted }}>-</span>;
  return (
    <ProjectBadgesContainer>
      {items.map(item => {
        if (item === 'Portal') {
          return <ProjectBadge key="portal" theme={theme} $color={ProjectScopeColors.Internal}>Portal</ProjectBadge>;
        }
        if (item === 'MobileApp') {
          return <ProjectBadge key="mobile" theme={theme} $color={ProjectScopeColors.External}>Mobile</ProjectBadge>;
        }
        return null;
      })}
    </ProjectBadgesContainer>
  );
};

const TasksPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useRole();
  const { user } = useAuth();
  const canEditTask = isAdmin() || !!user?.isInternalTaskOrganization;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [projectScopeFilter, setProjectScopeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('tasks_viewMode') || 'kanban'; } catch { return 'kanban'; }
  });
  const [myTasksOnly, setMyTasksOnly] = useState(() => {
    try {
      const stored = localStorage.getItem('tasks_myTasksOnly');
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  });
  const [showClosed, setShowClosed] = useState(() => {
    try { return localStorage.getItem('tasks_showClosed') === 'true'; } catch { return false; }
  });
  const [showBacklog, setShowBacklog] = useState(() => {
    try { return localStorage.getItem('tasks_showBacklog') === 'true'; } catch { return false; }
  });
  const [kanbanSortBy, setKanbanSortBy] = useState(() => {
    try { return localStorage.getItem('tasks_kanbanSortBy') || 'custom'; } catch { return 'custom'; }
  });

  const pageSize = viewMode === 'kanban' ? KANBAN_PAGE_SIZE : TABLE_PAGE_SIZE;

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await supportTaskService.getTasks({
        status: (viewMode === 'kanban') ? undefined : (statusFilter !== '' ? statusFilter : undefined),
        type: typeFilter !== '' ? typeFilter : undefined,
        priority: priorityFilter !== '' ? priorityFilter : undefined,
        projectScope: projectScopeFilter !== '' ? projectScopeFilter : undefined,
        search: searchTerm || undefined,
        myTasksOnly: myTasksOnly || undefined,
        excludeClosed: !showClosed && statusFilter === '' ? true : undefined,
        excludeBacklog: !showBacklog && statusFilter === '' ? true : undefined,
        sortBy: 'status',
        page: viewMode === 'kanban' ? 1 : currentPage,
        pageSize,
      });
      const data = response.data || response;
      setTasks(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, priorityFilter, projectScopeFilter, searchTerm, currentPage, pageSize, viewMode, myTasksOnly, showClosed, showBacklog]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]); // eslint-disable-line

  const handleDeleteTask = async (id) => {
    try {
      await supportTaskService.deleteTask(id);
      toast.success('Task deleted successfully');
      setDeleteConfirmId(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error(error.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await supportTaskService.updateTaskStatus(taskId, { status: newStatus });
      toast.success(`Task moved to ${getTaskStatusLabel(newStatus)}`);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const refetchTimerRef = useRef(null);

  const handleReorder = async (taskId, status, newPosition, { immediate } = {}) => {
    if (immediate) {
      // Optimistic: remove the task from local state immediately (for collapsed column drops
      // where @formkit/drag-and-drop doesn't visually move the card)
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status } : t));
    }
    try {
      await supportTaskService.reorderTask({ taskId, status, newPosition });
      if (immediate) {
        fetchTasks();
      } else {
        // Debounce refetch — the drag already shows the correct visual state.
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => fetchTasks(), 1500);
      }
    } catch (error) {
      console.error('Failed to reorder task:', error);
      toast.error('Failed to reorder task');
      fetchTasks();
    }
  };

  const handleKanbanSortChange = (newSort) => {
    setKanbanSortBy(newSort);
    try { localStorage.setItem('tasks_kanbanSortBy', newSort); } catch {}
  };

  const handleShowClosedToggle = () => {
    setShowClosed(prev => {
      const next = !prev;
      try { localStorage.setItem('tasks_showClosed', String(next)); } catch {}
      return next;
    });
    setCurrentPage(1);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('tasks_viewMode', mode); } catch {}
    setCurrentPage(1);
    // Clear status filter in kanban mode (all statuses shown as columns)
    if (mode === 'kanban') {
      setStatusFilter('');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const getAssignedToName = (task) => {
    if (task.assignedToUserName) return task.assignedToUserName;
    return '-';
  };

  const totalPages = Math.ceil(totalCount / TABLE_PAGE_SIZE);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * TABLE_PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * TABLE_PAGE_SIZE, totalCount);

  return (
    <Layout currentPath="/tasks">
      <PageContainer theme={theme} style={{ maxWidth: '100%' }}>
        <PageHeader theme={theme} style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#43474f', fontWeight: 500, marginBottom: 10 }}>
              <span>Operations</span>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>Tasks</span>
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.875rem', fontWeight: 800, color: '#232562', margin: '0 0 6px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Tasks</h1>
            <p style={{ color: '#43474f', fontSize: '0.875rem', margin: 0 }}>Manage support tasks and tickets</p>
          </div>
          <HeaderActions theme={theme}>
            <ViewToggleGroup theme={theme}>
              <ViewToggleButton
                theme={theme}
                $active={viewMode === 'table'}
                onClick={() => handleViewModeChange('table')}
                title="Table view"
              >
                Table
              </ViewToggleButton>
              <ViewToggleButton
                theme={theme}
                $active={viewMode === 'kanban'}
                onClick={() => handleViewModeChange('kanban')}
                title="Board view"
              >
                Board
              </ViewToggleButton>
            </ViewToggleGroup>
            {canEditTask && (
              <Button onClick={() => navigate('/tasks/new')}>
                + New Task
              </Button>
            )}
          </HeaderActions>
        </PageHeader>

        <FilterBar theme={theme}>
          <SearchInputWrapper>
            <SearchIconStyled theme={theme}><Search size={14} /></SearchIconStyled>
            <SearchInput
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </SearchInputWrapper>
          {viewMode === 'table' && (
            <FilterSelect
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              theme={theme}
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </FilterSelect>
          )}
          <FilterSelect
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
            theme={theme}
          >
            <option value="">All Types</option>
            {TYPE_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
            theme={theme}
          >
            <option value="">All Priorities</option>
            {PRIORITY_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={projectScopeFilter}
            onChange={(e) => { setProjectScopeFilter(e.target.value); setCurrentPage(1); }}
            theme={theme}
          >
            <option value="">All Projects</option>
            {PROJECT_SCOPE_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </FilterSelect>
          <FilterToggle
            theme={theme}
            $active={myTasksOnly}
            onClick={() => {
              setMyTasksOnly(prev => {
                const next = !prev;
                try { localStorage.setItem('tasks_myTasksOnly', String(next)); } catch {}
                return next;
              });
              setCurrentPage(1);
            }}
          >
            My Tasks
          </FilterToggle>
          <FilterToggle
            theme={theme}
            $active={showClosed}
            onClick={handleShowClosedToggle}
          >
            Closed
          </FilterToggle>
          <FilterToggle
            theme={theme}
            $active={showBacklog}
            onClick={() => {
              setShowBacklog(prev => {
                const next = !prev;
                try { localStorage.setItem('tasks_showBacklog', String(next)); } catch {}
                return next;
              });
              setCurrentPage(1);
            }}
          >
            Backlog
          </FilterToggle>
          {viewMode === 'kanban' && (
            <FilterSelect
              value={kanbanSortBy}
              onChange={(e) => handleKanbanSortChange(e.target.value)}
              theme={theme}
            >
              <option value="custom">Custom Order</option>
              <option value="priority">Priority</option>
              <option value="dueDate">Due Date</option>
              <option value="created">Created Date</option>
            </FilterSelect>
          )}
        </FilterBar>

        {viewMode === 'kanban' ? (
          loading ? (
            <div style={{ padding: theme.spacing.lg }}>
              <SkeletonTable rows={3} cols={4} theme={theme} />
            </div>
          ) : (
            <KanbanBoard
              tasks={tasks}
              onStatusChange={handleStatusChange}
              onReorder={handleReorder}
              onTaskClick={(id) => navigate(`/tasks/${id}`)}
              sortBy={kanbanSortBy}
            />
          )
        ) : (
          <TableContainer theme={theme}>
            {loading ? (
              <SkeletonTable rows={5} cols={6} theme={theme} />
            ) : tasks.length === 0 ? (
              <EmptyState
                theme={theme}
                icon={<ClipboardCheck size={48} />}
                title="No tasks found"
                description="No tasks match your current filters"
              />
            ) : (
              <>
                <Table>
                  <Thead theme={theme}>
                    <tr>
                      <Th theme={theme}>Task #</Th>
                      <Th theme={theme}>Title</Th>
                      <Th theme={theme}>Project</Th>
                      <Th theme={theme}>Type</Th>
                      <Th theme={theme}>Priority</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>Assigned To</Th>
                      <Th theme={theme}>Created</Th>
                      <Th theme={theme}>Due Date</Th>
                      <Th theme={theme} style={{ width: '50px' }}></Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {tasks.map(task => (
                      <Tr key={task.id} theme={theme} onClick={() => navigate(`/tasks/${task.id}`)}>
                        <Td theme={theme}>
                          <ThemedText type="defaultSemiBold" style={{ color: theme.colors.tint }}>
                            {task.taskNumber || task.id}
                          </ThemedText>
                        </Td>
                        <Td theme={theme}>
                          <ThemedText type="small">{task.title}</ThemedText>
                        </Td>
                        <Td theme={theme}>
                          {renderProjectBadges(task.projectScope, theme)}
                        </Td>
                        <Td theme={theme}>
                          <StatusBadge theme={theme} $color={getTypeColor(task.taskType)}>
                            {getTaskTypeLabel(task.taskType)}
                          </StatusBadge>
                        </Td>
                        <Td theme={theme}>
                          <StatusBadge theme={theme} $color={getPriorityColor(task.priority)}>
                            {getTaskPriorityLabel(task.priority)}
                          </StatusBadge>
                        </Td>
                        <Td theme={theme}>
                          <StatusBadge theme={theme} $color={getStatusColor(task.status)}>
                            {getTaskStatusLabel(task.status)}
                          </StatusBadge>
                        </Td>
                        <Td theme={theme}>
                          <ThemedText type="small">{getAssignedToName(task)}</ThemedText>
                        </Td>
                        <Td theme={theme}>
                          <ThemedText type="small">{formatDate(task.createdAt)}</ThemedText>
                        </Td>
                        <Td theme={theme}>
                          <ThemedText type="small">{formatDate(task.dueDate)}</ThemedText>
                        </Td>
                        <Td theme={theme} onClick={(e) => e.stopPropagation()}>
                          <ActionMenu actions={[
                            ...(canEditTask ? [{ label: 'Edit', onClick: () => navigate(`/tasks/${task.id}/edit`) }] : []),
                            ...(isSuperAdmin() ? [{ label: 'Delete', onClick: () => setDeleteConfirmId(task.id), variant: 'danger' }] : []),
                          ]} />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <PaginationContainer theme={theme}>
                  <ThemedText type="small">
                    Showing {startItem}-{endItem} of {totalCount}
                  </ThemedText>
                  <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </PaginationContainer>
              </>
            )}
          </TableContainer>
        )}

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Task"
          message="Are you sure you want to delete this task? This action cannot be undone."
          onConfirm={() => handleDeleteTask(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>
    </Layout>
  );
};

export default TasksPage;
export { TasksPage };
